import {noop} from '@benchling/js-utils';
import {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingState, useAppSelector} from 'redux-stores/reduceApp';

import {CollapsedTableState, selectTableCollapsed} from './reduceCollapsedTablesUI';

export function isTableCollapsed({
  collapseId,
  collapsedStateById,
  rowCount,
  cellCount,
  tableKind,
  autoCollapseLimit,
}: {
  collapseId: string;
  collapsedStateById: CollapsedTableState;
  rowCount: number;
  cellCount: number;
  tableKind: StructuredTableKind | null;
  autoCollapseLimit?: number;
}): boolean {
  return (
    collapsedStateById[collapseId] ??
    shouldCollapseTableInitially(rowCount, cellCount, tableKind, autoCollapseLimit)
  );
}

export function createInputTableCollapseId(stageEntryId: string): string {
  return `input-${stageEntryId}`;
}

export function createOutputTableCollapseId(stageEntryId: string): string {
  return `output-${stageEntryId}`;
}
export function getCollapseId({collapseId, cid}: {collapseId?: string; cid: string}): string {
  return collapseId || cid;
}

/**
 * Whether a table kind is exempt from collapsing (and therefore from being
 * substituted by a CSV export in read-only views).
 *
 * TODO: This helper is temporary and should be removed once
 * `ENABLE_MIXTURE_PREP_ENTRY_EVALUATION` is fully rolled out, after which callers
 * can go back to checking `STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE` directly.
 * Until the flag is on, Mixture ingredients tables do not participate in CSV
 * export, so they must not collapse; once it is on they collapse like other large
 * tables. Mixture metadata tables are permanently exempt (see
 * `STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE`).
 */
export function isTableKindExemptFromCollapse(tableKind: StructuredTableKind | null): boolean {
  if (
    tableKind === StructuredTableKind.MIXTURE_INGREDIENTS &&
    !BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION
  ) {
    return true;
  }
  return STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE.includes(tableKind);
}

export function getIsOverInlineRowLimit(numRows: number, tableKind: StructuredTableKind | null): boolean {
  const rowLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_ROW_LIMIT;
  return (
    !isTableKindExemptFromCollapse(tableKind) &&
    numRows > 0 &&
    Number.isInteger(rowLimit) &&
    rowLimit > -1 &&
    numRows > rowLimit
  );
}

export function getIsOverInlineCellLimit(numCells: number, tableKind: StructuredTableKind | null): boolean {
  const cellLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_CELL_LIMIT;
  return (
    !isTableKindExemptFromCollapse(tableKind) &&
    numCells > 0 &&
    Number.isInteger(cellLimit) &&
    cellLimit > -1 &&
    numCells > cellLimit
  );
}

export function shouldCollapseTableInitially(
  numRows: number,
  numCells: number,
  tableKind: StructuredTableKind | null,
  autoCollapseLimit?: number
): boolean {
  return (
    (!isTableKindExemptFromCollapse(tableKind) &&
      numRows > (autoCollapseLimit || BenchlingConfig.ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE)) ||
    getIsOverInlineRowLimit(numRows, tableKind) ||
    getIsOverInlineCellLimit(numCells, tableKind)
  );
}

function getIsPermaCollapsed({
  isOverInlineRowLimit,
  isOverInlineCellLimit,
  isEditViewAvailable,
  isReadonlyViewer,
  isSupportedByCsvExport,
}: {
  isOverInlineRowLimit: boolean;
  isOverInlineCellLimit: boolean;
  isEditViewAvailable: boolean;
  isReadonlyViewer: boolean;
  isSupportedByCsvExport: boolean;
}): boolean {
  if (isReadonlyViewer && !isSupportedByCsvExport) {
    return false;
  }
  if (isReadonlyViewer && (isOverInlineRowLimit || isOverInlineCellLimit)) {
    return true;
  }
  return false;
}

export function useTableCollapse({
  autoCollapseLimit,
  collapseId,
  cid,
  forceCollapsed,
  isFocused,
  isMaximized,
  isSplitScreen,
  isCollapsible,
  viewer,
  table,
  maximumHeight,
  setMaximizedTable,
  setSplitScreenTable,
}: Pick<
  TableEditableProps,
  | 'autoCollapseLimit'
  | 'collapseId'
  | 'cid'
  | 'forceCollapsed'
  | 'isFocused'
  | 'isMaximized'
  | 'isSplitScreen'
  | 'isCollapsible'
  | 'viewer'
  | 'table'
  | 'maximumHeight'
  | 'setMaximizedTable'
  | 'setSplitScreenTable'
>): {
  isCollapsed: boolean;
  isCollapsedInitially: boolean;
  isPermaCollapsed: boolean;
} {
  const id = getCollapseId({collapseId, cid});
  const tableKind = TableUtil.getTableKind(table);
  const isLegacyTable = TableUtil.isLegacyTable(table._tableType);
  const isReadonlyViewer = Boolean(viewer);
  const {rowCount, cellCount} = TableUtil.getTableDimensionsFromTable(table);

  const isCollapsedInitially =
    Boolean(isCollapsible) && shouldCollapseTableInitially(rowCount, cellCount, tableKind, autoCollapseLimit);

  const isOverInlineRowLimit = getIsOverInlineRowLimit(rowCount, tableKind);
  const isOverInlineCellLimit = getIsOverInlineCellLimit(cellCount, tableKind);

  const isEditViewAvailable =
    (setMaximizedTable != null && setMaximizedTable !== noop) ||
    (setSplitScreenTable != null && setSplitScreenTable !== noop);

  const isPermaCollapsed = getIsPermaCollapsed({
    isOverInlineRowLimit,
    isOverInlineCellLimit,
    isEditViewAvailable,
    isReadonlyViewer,
    isSupportedByCsvExport: !isLegacyTable,
  });

  const isCollapsedSelector = useCallback((state: BenchlingState) => selectTableCollapsed(state, id), [id]);

  const isCollapsed = useAppSelector(isCollapsedSelector);

  const getIsCollapsed = (): boolean => {
    if (forceCollapsed || isFocused) {
      return true;
    }

    if (isMaximized || isSplitScreen) {
      return false;
    }

    // Perma-collapse (a table that is exported as CSV in a read-only view) takes precedence
    // over `isCollapsible`. Group-contained tables (e.g. Mixture Prep ingredients tables) are
    // rendered with `isCollapsible: false`, but a large one must still collapse to its header +
    // CSV banner in the export/print PDF instead of rendering the full table.
    if (isPermaCollapsed) {
      return true;
    }

    if (!isCollapsible) {
      return false;
    }

    // Print and export view should render non-permacollapsed tables as expanded
    if (viewer) {
      return false;
    }

    return isCollapsed ?? isCollapsedInitially;
  };

  return {
    isCollapsed: getIsCollapsed(),
    isCollapsedInitially,
    isPermaCollapsed,
  };
}
